import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Heart, MapPin, Plus, UserRound, ListChecks, Mail, Settings, ChevronRight, HeartHandshake, Sparkles } from "lucide-react";
import { ZoneDialog } from "../components/Dialogs";
import { GoogleButton } from "../components/GoogleButton";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { useAppData } from "../context/AppDataContext";
import { useRegion } from "../context/RegionContext";
import { toast } from "sonner";
import { LegalLinks } from "../components/LegalLinks";
import { Lock } from "lucide-react";
import { api, fileUrl, mediaUrl } from "../lib/api";
import { useUnread } from "../context/UnreadContext";
import { BrandText } from "../components/ULoveULogo";

function Row({ icon: Icon, label, onClick, testid, badge, count }) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-4 rounded-2xl bg-white px-4 py-3.5 text-left shadow-[0_2px_12px_rgba(13,42,107,0.07)] transition-[transform,box-shadow] duration-200 hover:shadow-[0_6px_18px_rgba(13,42,107,0.12)] active:scale-[0.99]"
      data-testid={testid}
    >
      <Icon className="h-6 w-6 text-brand" strokeWidth={1.7} />
      <span className="flex-1 text-[16px] font-medium text-navy">{label}</span>
      {badge && <span className="rounded-full bg-mist px-2 py-0.5 text-[11px] font-semibold text-slate-500">{badge}</span>}
      {count > 0 && <span className="min-w-[1.5rem] rounded-full bg-alert px-2 py-0.5 text-center text-xs font-bold text-white" data-testid={`${testid}-badge`}>{count > 99 ? "99+" : count}</span>}
      <ChevronRight className="h-5 w-5 text-brand" />
    </button>
  );
}

function ContactosRow({ user, active }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const { guardCategory } = useAppData();
  const [profile, setProfile] = useState(undefined);
  const allowed = !!user.is_adult;

  useEffect(() => {
    if (!active || !allowed) return;
    api.get("/me/contactos-profile").then((r) => setProfile(r.data.profile)).catch(() => {});
  }, [active, allowed, user.user_id]);

  const open = () => {
    if (!allowed) return toast(t("gate.minorSub"));
    if (profile) return navigate(`/editar/${profile.id}`);
    guardCategory("contactos", () => navigate("/publicar?category=contactos"));
  };
  const n = profile?.photos?.length || 0;
  return (
    <button onClick={open} data-testid="profile-row-contactos"
      className="flex w-full items-center gap-4 rounded-2xl bg-white px-4 py-3.5 text-left shadow-[0_2px_12px_rgba(13,42,107,0.07)] transition-[transform,box-shadow] duration-200 hover:shadow-[0_6px_18px_rgba(13,42,107,0.12)] active:scale-[0.99]">
      <HeartHandshake className={`h-6 w-6 ${allowed ? "text-brand" : "text-slate-300"}`} strokeWidth={1.7} />
      <span className={`flex-1 text-[16px] font-medium ${allowed ? "text-navy" : "text-slate-400"}`}><BrandText text={t("profile.contactos")} /></span>
      {!allowed ? (
        <span className="inline-flex items-center gap-1 rounded-full bg-alert/10 px-2 py-0.5 text-[11px] font-extrabold text-alert" data-testid="profile-row-contactos-locked">
          <Lock className="h-3 w-3" /> +18
        </span>
      ) : profile ? (
        <span className="flex items-center gap-2" data-testid="profile-row-contactos-status">
          <span className={`text-[11px] font-extrabold ${n < 8 ? "text-alert" : "text-brand"}`} data-testid="profile-row-contactos-photos">{t("form.photoProgress", { n, m: 8 })}</span>
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${profile.status === "active" ? "bg-brand/15 text-brand" : "bg-slate-200 text-slate-600"}`}>
            {t(profile.status === "active" ? "mine.active" : "mine.paused")}
          </span>
          {profile.photos[0] && <img src={fileUrl(profile.photos[0])} alt="" className="h-9 w-9 rounded-lg object-cover" />}
        </span>
      ) : profile === null ? (
        <span className="text-xs font-bold text-brand" data-testid="profile-row-contactos-create">{t("profile.createContactos")}</span>
      ) : null}
      <ChevronRight className="h-5 w-5 text-brand" />
    </button>
  );
}

function ActivityCard({ unread, onOpen, t }) {
  const items = [
    { key: "messages", tab: "chats", icon: Mail, n: unread.messages },
    { key: "requests", tab: "requests", icon: HeartHandshake, n: unread.requests },
    { key: "matches", tab: "matches", icon: Sparkles, n: unread.matches },
  ];
  return (
    <div id="profile-activity" className="mt-5 scroll-mt-4 rounded-2xl bg-white p-4 shadow-[0_2px_12px_rgba(13,42,107,0.07)]" data-testid="profile-activity">
      <p className="mb-3 text-sm font-extrabold uppercase tracking-wide text-navy">{t("activity.title")}</p>
      <div className="grid grid-cols-3 gap-2">
        {items.map(({ key, tab, icon: Icon, n }) => (
          <button key={key} onClick={() => onOpen(tab)} data-testid={`profile-activity-${key}`}
            className={`flex flex-col items-center gap-1 rounded-xl px-1 py-3 transition-[transform,background-color] duration-200 active:scale-95 ${n > 0 ? "bg-alert/10" : "bg-mist"}`}>
            <Icon className={`h-5 w-5 ${n > 0 ? "text-alert" : "text-brand"}`} strokeWidth={2} />
            <span className={`text-2xl font-extrabold leading-none ${n > 0 ? "text-alert" : "text-navy"}`} data-testid={`profile-activity-${key}-count`}>{n > 99 ? "99+" : n}</span>
            <span className="text-[11px] font-semibold text-navy">{t(`activity.${key}`)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ProfileScreen({ active }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { zone, zoneName, requireLogin } = useAppData();
  const [zoneOpen, setZoneOpen] = useState(false);
  const { region } = useRegion();
  const { unread, refresh } = useUnread();
  const guard = (path) => () => requireLogin() && navigate(path);
  const avatar = user ? mediaUrl(user.avatar_url) : null;
  const focus = active && location.state?.focus === "activity";

  useEffect(() => {
    if (!focus) return undefined;
    refresh();
    const id = setTimeout(() => document.getElementById("profile-activity")?.scrollIntoView({ behavior: "smooth", block: "start" }), 450);
    return () => clearTimeout(id);
  }, [focus, location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-full bg-mist pb-16" data-testid="profile-screen">
      <div className="bg-brand pb-[8.5rem] pt-safe-3 text-white">
        <div className="mx-auto flex max-w-xl items-start justify-between px-4">
          <button onClick={() => navigate("/mercat")} className="rounded-full p-2 transition-colors hover:bg-white/15" aria-label="back" data-testid="profile-back-btn">
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="pt-1 text-center">
            <div className="text-xl font-extrabold" data-testid="profile-brand">{region.brand_title}</div>
            <div className="text-sm text-white/85">{t("profile.title")}</div>
          </div>
          <button onClick={guard("/favoritos")} className="rounded-full p-2 transition-colors hover:bg-white/15" aria-label="favorites" data-testid="profile-favorites-icon-btn">
            <Heart className="h-6 w-6" />
          </button>
        </div>
      </div>

      <div className="mx-auto -mt-16 max-w-xl px-4">
        <div className="relative rounded-2xl bg-white px-5 pb-5 pt-20 text-center shadow-[0_6px_24px_rgba(13,42,107,0.1)]">
          <div className="absolute -top-16 left-1/2 h-32 w-32 -translate-x-1/2 overflow-hidden rounded-full border-4 border-white bg-mist shadow-lg" data-testid="profile-avatar">
            {avatar ? (
              <img src={avatar} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-brand/20 to-navy/10">
                <span className="text-4xl font-extrabold text-navy">{user?.name?.[0]?.toUpperCase() || <UserRound className="h-12 w-12 text-navy/50" />}</span>
              </div>
            )}
          </div>
          <h2 className="text-3xl font-extrabold text-navy" data-testid="profile-name">{user ? user.name : t("profile.visitor")}</h2>
          {user ? (
            <button onClick={() => setZoneOpen(true)} className="mt-2 inline-flex items-center gap-1.5 text-[15px] text-navy" data-testid="profile-zone-btn">
              <MapPin className="h-5 w-5 text-alert" />
              {t("profile.defaultZone")} <span className="font-semibold text-brand">{zone === "all" ? t("zone.allRegion") : zoneName(zone)}</span>
            </button>
          ) : (
            <>
              <p className="mt-1 text-sm text-slate-500">{t("profile.visitorSub")}</p>
              <div className="mt-4 grid gap-2.5">
                <button className="btn-primary" onClick={() => navigate("/login")} data-testid="profile-login-btn">{t("auth.login")}</button>
                <button className="btn-outline" onClick={() => navigate("/registro")} data-testid="profile-register-btn">{t("auth.register")}</button>
                <GoogleButton testid="profile-google-btn" />
              </div>
            </>
          )}
        </div>

        {user && <ActivityCard unread={unread} onOpen={(tab) => navigate(`/mensajes?tab=${tab}`)} t={t} />}

        <button
          onClick={guard("/publicar")}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-navy py-4 text-lg font-bold text-white shadow-[0_8px_20px_rgba(13,42,107,0.3)] transition-[transform,background-color] duration-200 hover:bg-[#123787] active:scale-[0.98]"
          data-testid="profile-publish-btn"
        >
          <Plus className="h-5 w-5" strokeWidth={3} /> {t("profile.publish")}
        </button>

        <div className="mt-5 space-y-3">
          <Row icon={UserRound} label={t("profile.myData")} onClick={guard("/mis-datos")} testid="profile-row-mydata" />
          {user && <ContactosRow user={user} active={active} />}
          <Row icon={ListChecks} label={t("profile.myListings")} onClick={guard("/mis-anuncios")} testid="profile-row-mylistings" />
          <Row icon={Heart} label={t("profile.favorites")} onClick={guard("/favoritos")} testid="profile-row-favorites" />
          <Row icon={Mail} label={t("profile.messages")} onClick={guard("/mensajes")} testid="profile-row-messages" count={unread.total} />
          <Row icon={Settings} label={t("profile.settings")} onClick={() => navigate("/ajustes")} testid="profile-row-settings" />
        </div>

        <p className="mt-6 px-4 text-center text-xs leading-relaxed text-slate-500" data-testid="profile-note">
          {t("profile.note1")}<span className="font-bold text-alert">{t("profile.note2")}</span>
        </p>

        {user && (
          <button
            onClick={async () => { await logout(); navigate("/"); }}
            className="mt-5 w-full rounded-full bg-alert py-4 text-lg font-bold text-white shadow-[0_8px_20px_rgba(229,57,53,0.3)] transition-[transform,opacity] duration-200 hover:opacity-90 active:scale-[0.98]"
            data-testid="profile-logout-btn"
          >
            {t("profile.logout")}
          </button>
        )}
        <div className="mt-8 border-t border-slate-200 pt-5" data-testid="profile-legal">
          <p className="mb-2 text-center text-[11px] font-extrabold uppercase tracking-widest text-slate-400">{t("legal.section")}</p>
          <LegalLinks testid="profile-legal-links" />
        </div>
      </div>
      <ZoneDialog open={zoneOpen} onOpenChange={setZoneOpen} />
    </div>
  );
}
